import { looks as rules } from "@app/domain";
import {
  type Access,
  type ChosenLooks,
  type Look,
  type LooksResponse,
  looksResponseSchema,
} from "@app/protocol";
import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../config";
import { useStore } from "../store";

export interface LooksState {
  /** A look's access for this player; Club looks read undefined until the server answered. */
  accessOf: (look: Look) => Access | undefined;
  notified: (look: Look) => boolean;
  /** Looks whose trial is being recorded right now, by `<kind>.<id>`. */
  pending: ReadonlySet<string>;
  /** Server time minus ours: a trial's end is the server's clock (review 0015 U9). */
  skew: number;
  /** The scene and deck the player's next table starts with; null until the server answered. */
  chosen: ChosenLooks | null;
  startTrial: (look: Look) => Promise<boolean>;
  notify: (look: Look) => Promise<void>;
  /** Stores the player's pick for their next table; the server keeps only a look they may use. */
  remember: (look: Look) => void;
}

async function call(session: string, path: string, method: "GET" | "POST") {
  try {
    const response = await fetch(`${API_BASE}/looks${path}`, {
      method,
      headers: { Authorization: `Bearer ${session}` },
    });
    if (!response.ok) return null;
    const parsed = looksResponseSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

const same = (a: Look, b: Look) => a.kind === b.kind && a.id === b.id;

/** Every Club scene and deck with what it means for this player (ADR 0029, 0030). */
export function useLooks(): LooksState {
  const session = useStore((s) => s.session);
  const [state, setState] = useState<{
    looks: LooksResponse["looks"] | null;
    chosen: ChosenLooks | null;
    skew: number;
  }>({ looks: null, chosen: null, skew: 0 });
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  const take = useCallback((found: LooksResponse | null) => {
    if (found !== null)
      setState({ looks: found.looks, chosen: found.chosen, skew: found.now - Date.now() });
    return found;
  }, []);
  useEffect(() => {
    if (session !== null) void call(session, "", "GET").then(take);
  }, [session, take]);
  const entry = (look: Look) => state.looks?.find((row) => same(row.look, look));
  const startTrial = useCallback(
    async (look: Look) => {
      if (session === null) return false;
      const key = rules.lookKey(look);
      setPending((now) => new Set([...now, key]));
      try {
        const found = take(await call(session, `/${look.kind}/${look.id}/trial`, "POST"));
        const kind = found?.looks.find((row) => same(row.look, look))?.access.kind;
        return kind === "trial" || kind === "club";
      } finally {
        setPending((now) => new Set([...now].filter((other) => other !== key)));
      }
    },
    [session, take],
  );
  const notify = useCallback(
    async (look: Look) => {
      if (session !== null) take(await call(session, `/${look.kind}/${look.id}/notify`, "POST"));
    },
    [session, take],
  );
  const remember = useCallback(
    (look: Look) => {
      if (session !== null)
        void call(session, `/${look.kind}/${look.id}/choose`, "POST").then(take);
    },
    [session, take],
  );
  return {
    accessOf: (look) => (rules.isClub(look) ? entry(look)?.access : { kind: "free" }),
    notified: (look) => entry(look)?.notified ?? false,
    pending,
    skew: state.skew,
    chosen: state.chosen,
    startTrial,
    notify,
    remember,
  };
}
